'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { RotateCcw } from 'lucide-react';

export default function RefundPolicyPage() {
  return (
    <AppLayout activeRoute="/refund-policy">
      <div className="max-w-3xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
            <RotateCcw size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Refund &amp; Cancellation Policy</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Effective Date: 21 May 2026</p>
          </div>
        </div>

        <div className="card-base p-8 space-y-8 text-sm text-muted-foreground leading-relaxed">
          <p className="text-foreground/80">
            This Refund &amp; Cancellation Policy governs purchases and subscription services offered through PropDataX.
          </p>

          <Section title="1. Subscription Services">
            <p>
              PropDataX may offer paid subscriptions, premium tools, analytics services, or software access.
            </p>
            <p className="mt-3">
              By purchasing a subscription, users agree to the pricing, billing cycle, and usage terms displayed during checkout.
            </p>
          </Section>

          <Section title="2. Refund Eligibility">
            <p>Refunds may be considered under the following circumstances:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Duplicate payment transactions</li>
              <li>Failed transactions where payment was deducted but service was not activated</li>
              <li>Technical errors caused by the platform</li>
            </ul>
            <p className="mt-3">Refund requests must generally be submitted within 7 days of the transaction date.</p>
          </Section>

          <Section title="3. Non-Refundable Situations">
            <p>Refunds may not be provided for:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Change of mind after purchase</li>
              <li>Partial usage of subscription period</li>
              <li>Failure to use purchased services</li>
              <li>Violations of platform terms</li>
              <li>Services already delivered or consumed</li>
            </ul>
          </Section>

          <Section title="4. Cancellation Policy">
            <p>Users may cancel recurring subscriptions before the next billing cycle.</p>
            <p className="mt-3">Upon cancellation:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Access remains active until the end of the paid billing period</li>
              <li>No additional charges will be applied after cancellation</li>
            </ul>
          </Section>

          <Section title="5. Refund Processing Time">
            <p>
              Approved refunds are generally processed within 7–14 business days depending on banking and payment gateway timelines.
            </p>
          </Section>

          <Section title="6. Contact for Refund Requests">
            <p>For refund or billing support:</p>
            <div className="mt-3 p-4 bg-muted/40 rounded-lg border border-border space-y-1">
              <p>Email: <a href="mailto:sales@propdatax.com" className="text-amber-400 hover:text-amber-300 transition-colors">sales@propdatax.com</a></p>
              <p>Website: <a href="https://propdatax.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition-colors">https://propdatax.com</a></p>
            </div>
          </Section>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold text-foreground border-b border-border pb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
